// =============================================================================
// Target Attribute Definitions
//
// Provides the attribute list shown in the modifier effect editor dropdown.
// Stored as a plain string in DynamoDB so custom attributes are always
// supported — this list is only used to drive the UI picker.
// =============================================================================
import type { RuleSystem } from '../../../backend/shared/types/index';

export interface AttributeOption {
  value:      string;   // normalised key stored in DB, e.g. 'save.fort'
  label:      string;   // human display, e.g. 'Fortitude Save'
  group:      string;   // for grouping in the dropdown
  systems:    RuleSystem[] | 'all';
}

export const COMBAT_ATTRIBUTES: readonly AttributeOption[] = [
  // ── Ability Scores ──────────────────────────────────────────────────────
  { value: 'str',         label: 'Strength',           group: 'Ability Scores', systems: 'all' },
  { value: 'dex',         label: 'Dexterity',          group: 'Ability Scores', systems: 'all' },
  { value: 'con',         label: 'Constitution',       group: 'Ability Scores', systems: 'all' },
  { value: 'int',         label: 'Intelligence',       group: 'Ability Scores', systems: 'all' },
  { value: 'wis',         label: 'Wisdom',             group: 'Ability Scores', systems: 'all' },
  { value: 'cha',         label: 'Charisma',           group: 'Ability Scores', systems: 'all' },

  // ── Armor Class ─────────────────────────────────────────────────────────
  { value: 'ac',              label: 'Armor Class',          group: 'Defense', systems: 'all' },
  { value: 'ac.touch',        label: 'Touch AC',             group: 'Defense', systems: ['dnd35', 'pathfinder1'] },
  { value: 'ac.flat_footed',  label: 'Flat-Footed AC',       group: 'Defense', systems: ['dnd35', 'pathfinder1'] },
  { value: 'hp.max',          label: 'Max Hit Points',       group: 'Defense', systems: 'all' },

  // ── Saving Throws ───────────────────────────────────────────────────────
  { value: 'save.fort',  label: 'Fortitude Save',    group: 'Saving Throws', systems: ['dnd35', 'pathfinder1'] },
  { value: 'save.ref',   label: 'Reflex Save',        group: 'Saving Throws', systems: ['dnd35', 'pathfinder1'] },
  { value: 'save.will',  label: 'Will Save',          group: 'Saving Throws', systems: ['dnd35', 'pathfinder1'] },
  { value: 'save.str',   label: 'Strength Save',      group: 'Saving Throws', systems: ['dnd5e'] },
  { value: 'save.dex',   label: 'Dexterity Save',     group: 'Saving Throws', systems: ['dnd5e'] },
  { value: 'save.con',   label: 'Constitution Save',  group: 'Saving Throws', systems: ['dnd5e'] },
  { value: 'save.int',   label: 'Intelligence Save',  group: 'Saving Throws', systems: ['dnd5e'] },
  { value: 'save.wis',   label: 'Wisdom Save',        group: 'Saving Throws', systems: ['dnd5e'] },
  { value: 'save.cha',   label: 'Charisma Save',      group: 'Saving Throws', systems: ['dnd5e'] },
  { value: 'save.all',   label: 'All Saving Throws',  group: 'Saving Throws', systems: 'all' },

  // ── Attack ──────────────────────────────────────────────────────────────
  { value: 'attack',         label: 'All Attack Rolls',    group: 'Attack', systems: 'all' },
  { value: 'attack.melee',   label: 'Melee Attack Rolls',  group: 'Attack', systems: 'all' },
  { value: 'attack.ranged',  label: 'Ranged Attack Rolls', group: 'Attack', systems: 'all' },
  { value: 'attack.spell',   label: 'Spell Attack Rolls',  group: 'Attack', systems: 'all' },

  // ── Damage ──────────────────────────────────────────────────────────────
  { value: 'damage',         label: 'All Damage',          group: 'Damage', systems: 'all' },
  { value: 'damage.melee',   label: 'Melee Damage',        group: 'Damage', systems: 'all' },
  { value: 'damage.ranged',  label: 'Ranged Damage',       group: 'Damage', systems: 'all' },
  { value: 'damage.spell',   label: 'Spell Damage',        group: 'Damage', systems: 'all' },

  // ── Combat Maneuvers (PF1 / 3.5) ────────────────────────────────────────
  { value: 'cmb',  label: 'Combat Maneuver Bonus (CMB)', group: 'Combat Maneuvers', systems: ['pathfinder1'] },
  { value: 'cmd',  label: 'Combat Maneuver Defense (CMD)', group: 'Combat Maneuvers', systems: ['pathfinder1'] },
  { value: 'grapple', label: 'Grapple Check', group: 'Combat Maneuvers', systems: ['dnd35'] },

  // ── Movement ────────────────────────────────────────────────────────────
  { value: 'speed',       label: 'Base Speed',   group: 'Movement', systems: 'all' },
  { value: 'speed.fly',   label: 'Fly Speed',    group: 'Movement', systems: 'all' },
  { value: 'speed.swim',  label: 'Swim Speed',   group: 'Movement', systems: 'all' },

  // ── Other ───────────────────────────────────────────────────────────────
  { value: 'initiative',  label: 'Initiative',   group: 'Other', systems: 'all' },
  { value: 'caster_level',label: 'Caster Level', group: 'Other', systems: ['dnd35', 'pathfinder1'] },
  { value: 'spell_dc',    label: 'Spell Save DC (all)', group: 'Other', systems: 'all' },
  { value: 'concentration', label: 'Concentration',  group: 'Other', systems: ['dnd35', 'pathfinder1'] },
];

/** Return attributes applicable to a given system (plus 'all' entries). */
export function attributesForSystem(system: RuleSystem): AttributeOption[] {
  return COMBAT_ATTRIBUTES.filter(
    (a) => a.systems === 'all' || (a.systems as RuleSystem[]).includes(system),
  );
}

/** Group attributes by their `group` field for use in a `<select>` with `<optgroup>`. */
export function groupedAttributes(system: RuleSystem): Record<string, AttributeOption[]> {
  const filtered = attributesForSystem(system);
  return filtered.reduce<Record<string, AttributeOption[]>>((acc, attr) => {
    (acc[attr.group] ??= []).push(attr);
    return acc;
  }, {});
}
